import React, { Fragment, useState, useRef, useEffect } from "react";
import { Collapse, Navbar, NavbarToggler, Nav, NavItem } from "reactstrap";
import { Link, useLocation } from "react-router";
import { useTranslation } from "react-i18next";

import logo from "../../../../resources/images/logo.png";
import "./styles/style.css";

import ExpandableSearch from "./components/search/components/expandable-search";
import LanguageSelector from "./components/search/components/language-selector";
import ProfileDropdown from "./components/profile-dropdown";
import CartProgress from "./components/cart-progress";
import Notice from "./components/notice";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { language: currentLanguage } = i18n;
  const location = useLocation();
  const navRef = useRef(null);
  const indicatorRef = useRef(null);

  const toggle = () => setIsOpen(!isOpen);

  const isActive = (path) => location.pathname.startsWith(path);

  const navItems = [
    { path: "/literature", show: true },
    { path: "/periodicals", show: currentLanguage !== "fr" },
    { path: "/courses", show: currentLanguage !== "fr" },
    { path: "/audio", show: true },
    { path: "/about", show: true },
    { path: "/donate", show: true },
  ];

  const updateIndicator = () => {
    if (!navRef.current || !indicatorRef.current) return;

    const activeLink = navRef.current.querySelector(".nav-link-icon.active, .nav-link-text.active");
    if (activeLink) {
      const navRect = navRef.current.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      indicatorRef.current.style.width = `${linkRect.width}px`;
      indicatorRef.current.style.left = `${linkRect.left - navRect.left}px`;
      indicatorRef.current.style.opacity = "1";
    } else {
      indicatorRef.current.style.opacity = "0";
    }
  };

  useEffect(() => {
    // Small delay to ensure DOM is ready after route change
    const timeoutId = setTimeout(updateIndicator, 100);
    window.addEventListener("resize", updateIndicator);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateIndicator);
    };
  }, [location.pathname, currentLanguage]);

  // Update indicator when collapse state changes (after animation)
  useEffect(() => {
    const timeoutId = setTimeout(updateIndicator, 350);
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  return (
    <Fragment>
      <div className="main-header">
        <header>
          <Navbar dark expand="lg" className="single-line-header px-3">
            <Link to="/" className="navbar-brand">
              <img
                className="img-fluid"
                src={logo}
                alt="Chapel Library Home"
                width="180"
              />
            </Link>

            <div className="header-search d-none d-lg-flex">
              <ExpandableSearch />
            </div>

            <div className="d-none d-lg-block">
              <CartProgress />
            </div>

            <NavbarToggler onClick={toggle} aria-label="Show or hide menu" />

            <Collapse isOpen={isOpen} navbar>
              <div className="nav-wrapper ms-auto" ref={navRef}>
                <div className="nav-indicator" ref={indicatorRef} />
                <Nav className="align-items-center" navbar>
                  <NavItem className="d-lg-none">
                    <ExpandableSearch />
                  </NavItem>
                  <NavItem className="d-lg-none w-100">
                    <CartProgress />
                  </NavItem>
                  <NavItem>
                    <Link onClick={toggle} to="/literature" className={`nav-link nav-link-text ${isActive("/literature") ? "active" : ""}`}>
                      {t("literature")}
                    </Link>
                  </NavItem>
                  {!(currentLanguage === "fr") && (
                    <NavItem>
                      <Link onClick={toggle} to="/periodicals" className={`nav-link nav-link-text ${isActive("/periodicals") ? "active" : ""}`}>
                        {t("periodicals")}
                      </Link>
                    </NavItem>
                  )}
                  {!(currentLanguage === "fr") && (
                    <NavItem>
                      <Link onClick={toggle} to="/courses" className={`nav-link nav-link-text ${isActive("/courses") ? "active" : ""}`}>
                        {t("courses")}
                      </Link>
                    </NavItem>
                  )}
                  <NavItem>
                    <Link onClick={toggle} to="/audio" className={`nav-link nav-link-text ${isActive("/audio") ? "active" : ""}`}>
                      {t("audio")}
                    </Link>
                  </NavItem>
                  <NavItem>
                    <Link onClick={toggle} to="/about" className={`nav-link nav-link-text ${isActive("/about") ? "active" : ""}`}>
                      {t("about")}
                    </Link>
                  </NavItem>
                  <NavItem>
                    <Link onClick={toggle} to="/donate" className={`nav-link nav-link-text donate-cta ${isActive("/donate") ? "active" : ""}`}>
                      {t("donate")}
                    </Link>
                  </NavItem>
                  <NavItem>
                    <ProfileDropdown />
                  </NavItem>
                  <NavItem>
                    <Link onClick={toggle} to="/cart" className={`nav-link nav-link-icon ${isActive("/cart") ? "active" : ""}`}>
                      <i className="fa fa-shopping-cart" />
                      <span>{t("cart")}</span>
                    </Link>
                  </NavItem>
                  <NavItem>
                    <LanguageSelector />
                  </NavItem>
                </Nav>
              </div>
            </Collapse>
          </Navbar>
        </header>
      </div>
      <Notice />
    </Fragment>
  );
};

export default Header;
